import type { AppDispatch } from '../../../store';
import type { NodeBase } from '../../../entities/Node';
import { formatItemNo } from '../../../utils/data/utils';
import { destructId } from '../../../utils/tree/treeNode';
import { moveItemNodes } from '../item/moveItems';

interface DestructIdResult {
  categoryId?: string;
  catalogId?: string;
  value?: string;
}

interface PasteMoveItemParams {
  newNodes: NodeBase[];
  targetNode: NodeBase;
  sourceNode: NodeBase;
  destinationName: string;
}

interface PasteMoveItemResult {
  nodesText: string;
  filteredNodes: NodeBase[];
}

export const pasteMoveItem = async (
  dispatch: AppDispatch,
  { newNodes, targetNode, sourceNode, destinationName }: PasteMoveItemParams,
): Promise<PasteMoveItemResult> => {
  const targetDestructed = destructId(targetNode.id) as DestructIdResult;
  const sourceDestructed = destructId(sourceNode.parentId) as DestructIdResult;

  const response = await dispatch(
    moveItemNodes({
      nodes: newNodes,
      catalogId: targetNode.catalogId,
      categoryId: targetDestructed.categoryId,
      fromCategoryId: sourceDestructed.categoryId,
    }),
  ).unwrap();

  const {
    addedItems = [],
    existingItems = [],
    notValidForStructureItems = [],
  } = response;

  // Filter out invalid nodes
  const filteredNodes = newNodes.filter(
    (node) =>
      !notValidForStructureItems?.some((nvfsi) => {
        return node?.item?.itemNo === nvfsi?.itemNo;
      }),
  );

  const addedItemNos = addedItems?.map((item) => ({ itemNo: item?.itemNo }));
  const existingItemNos = existingItems.map((item) => ({ itemNo: item?.itemNo }));
  const notValidForStructureItemNos = notValidForStructureItems.map((item) => ({
    itemNo: item?.itemNo,
  }));

  // Build the result text based on the response

  const nodesText = buildItemResultText({
    addedItems: addedItemNos || [],
    existingItems: existingItemNos || [],
    notValidForStructureItems: notValidForStructureItemNos || [],
    destinationName,
  });

  return {
    nodesText,
    filteredNodes,
  };
};

interface BuildTextParams {
  addedItems: { itemNo?: string }[];
  existingItems: { itemNo?: string }[];
  notValidForStructureItems: { itemNo?: string }[];
  destinationName: string;
}

const buildItemResultText = ({
  addedItems,
  existingItems,
  notValidForStructureItems,
  destinationName,
}: BuildTextParams): string => {
  const parts: string[] = [];

  if (existingItems.length === 1) {
    parts.push(
      `${formatItemNo(existingItems[0]?.itemNo || '')}</b> already exists in <b>${destinationName}`,
    );
  } else if (existingItems.length > 1) {
    parts.push(
      `${existingItems.length} items</b> already exist in <b>${destinationName}`,
    );
  }

  if (notValidForStructureItems.length === 1) {
    parts.push(
      `${formatItemNo(notValidForStructureItems[0]?.itemNo || '')}</b> is not valid for structure`,
    );
  } else if (notValidForStructureItems.length > 1) {
    parts.push(
      `${notValidForStructureItems.length} items</b> are not valid for structure`,
    );
  }

  if (addedItems.length === 0) {
    parts.push('no items');
  } else if (addedItems.length === 1) {
    parts.push(formatItemNo(addedItems[0]?.itemNo || ''));
  } else {
    parts.push(`${addedItems.length} items`);
  }

  if (
    existingItems.length === 0 &&
    addedItems.length === 0 &&
    notValidForStructureItems.length === 0
  ) {
    return 'no items';
  }

  return parts.join('\n<b>');
};
